import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, Navigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { useInvitation } from '@/hooks/useInvitation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Eye, EyeOff, Users, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { PasswordStrengthIndicator, PASSWORD_MIN_LENGTH, SPECIAL_CHAR_REGEX, isPasswordValid } from '@/components/auth/PasswordStrengthIndicator';
import { PasswordGeneratorButton } from '@/components/auth/PasswordGenerator';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { translateAuthError } from '@/lib/authErrors';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { CenteredSpinner } from '@/components/ui/centered-spinner';
import { GoogleAuthButton } from '@/components/auth/GoogleAuthButton';

const signupSchema = z.object({
  firstName: z.string().trim().min(1, { message: "Le prénom est requis" }).max(50, { message: "Le prénom est trop long" }),
  lastName: z.string().trim().min(1, { message: "Le nom est requis" }).max(50, { message: "Le nom est trop long" }),
  email: z.string().trim().email({ message: "Adresse email invalide" }),
  password: z.string()
    .min(PASSWORD_MIN_LENGTH, { message: `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères` })
    .refine((pwd) => /[A-Z]/.test(pwd), { message: "Le mot de passe doit contenir une majuscule" })
    .refine((pwd) => /[a-z]/.test(pwd), { message: "Le mot de passe doit contenir une minuscule" })
    .refine((pwd) => /[0-9]/.test(pwd), { message: "Le mot de passe doit contenir un chiffre" })
    .refine((pwd) => SPECIAL_CHAR_REGEX.test(pwd), { message: "Le mot de passe doit contenir un caractère spécial" }),
});

const Signup = () => {
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; email?: string; password?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  
  const { user, loading, signUp, signUpWithInvitation, signInWithGoogle } = useAuth();
  const { invitation, isLoading: invitationLoading, error: invitationError, acceptInvitation } = useInvitation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const hasInviteParams = searchParams.has('invite') && searchParams.has('token');
  
  usePageTitle(invitation ? `Rejoindre ${invitation.workspaceName}` : 'Inscription');

  // SEO meta tags
  useDocumentHead({
    title: invitation 
      ? `Rejoindre ${invitation.workspaceName} - Luminjo`
      : 'Créer un compte - Luminjo',
    description: 'Créez votre compte Luminjo et commencez à gérer vos tickets de support client en quelques minutes.',
  });

  // Pre-fill email from invitation
  useEffect(() => {
    if (invitation?.email && !email) {
      setEmail(invitation.email);
    }
  }, [invitation?.email, email]);

  // Handle logged-in user with invitation
  useEffect(() => {
    const handleLoggedInUser = async () => {
      // Don't retry if we already have an error or are already processing
      if (acceptError || isAcceptingInvitation) return;
      
      if (user && !loading && invitation?.isValid) {
        setIsAcceptingInvitation(true);
        
        const result = await acceptInvitation();
        
        if (result.success) {
          toast({
            title: 'Bienvenue !',
            description: `Vous avez rejoint l'espace "${invitation.workspaceName}".`,
          });
          // Clear URL params and navigate
          navigate('/', { replace: true });
        } else {
          // Store error to prevent retry loop and show error screen
          setAcceptError(result.error || 'Impossible de rejoindre l\'espace.');
          setIsAcceptingInvitation(false);
        }
      } else if (user && !loading && !hasInviteParams) {
        navigate('/');
      }
    };

    handleLoggedInUser();
  }, [user, loading, invitation, hasInviteParams, acceptInvitation, navigate, toast, isAcceptingInvitation, acceptError]);

  const validateForm = () => {
    const result = signupSchema.safeParse({ firstName, lastName, email, password });
    if (!result.success) {
      const fieldErrors: { firstName?: string; lastName?: string; email?: string; password?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === 'firstName') fieldErrors.firstName = err.message;
        if (err.path[0] === 'lastName') fieldErrors.lastName = err.message;
        if (err.path[0] === 'email') fieldErrors.email = err.message;
        if (err.path[0] === 'password') fieldErrors.password = fieldErrors.password || err.message;
      });
      setErrors(fieldErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Double-check password strength
    if (!isPasswordValid(password)) {
      setErrors({ password: "Le mot de passe ne respecte pas tous les critères" });
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Use the invitation flow if we have a valid invitation
      if (invitation?.isValid) {
        const { error, session, userExists } = await signUpWithInvitation(
          email, 
          password, 
          invitation.workspaceId, 
          invitation.token,
          firstName,
          lastName
        );
        
        if (error) {
          if (userExists) {
            toast({
              title: "Compte existant",
              description: `Un compte existe déjà avec cet email. Connectez-vous pour rejoindre "${invitation.workspaceName}".`,
            });
            const params = new URLSearchParams({
              invite: invitation.workspaceId,
              token: invitation.token,
            });
            navigate(`/auth?${params.toString()}`, { replace: true });
          } else {
            toast({
              title: "Erreur d'inscription",
              description: translateAuthError(error.message),
              variant: "destructive",
            });
          }
          setIsSubmitting(false);
          return;
        }
        
        // Success! User is logged in automatically when email confirmation is skipped
        if (session) {
          toast({
            title: 'Bienvenue !',
            description: `Votre compte a été créé et vous avez rejoint "${invitation.workspaceName}".`,
          });
          navigate('/', { replace: true });
        } else {
          // Email confirmation still required: invitation est acceptée côté BDD
          // mais l'accès sera activé une fois l'email confirmé.
          const params = new URLSearchParams({
            email,
            workspace: invitation.workspaceName,
          });
          navigate(`/invitation/pending?${params.toString()}`, { replace: true });
        }
        return;
      }
      
      // Standard signup flow (no invitation)
      const { error, userAlreadyExists } = await signUp(email, password, firstName, lastName);
      
      if (error) {
        toast({
          title: "Erreur d'inscription",
          description: translateAuthError(error.message),
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      if (userAlreadyExists) {
        toast({
          title: "Compte existant",
          description: "Un compte existe déjà avec cet email. Veuillez vous connecter.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      // Redirect to confirmation page for standard signup
      const confirmUrl = `/signup/confirmation?email=${encodeURIComponent(email)}`;
      navigate(confirmUrl);
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const handlePasswordGenerated = (generatedPassword: string) => {
    setPassword(generatedPassword);
    setShowPassword(true);
    setErrors((prev) => ({ ...prev, password: undefined }));
  };

  // Show loading state
  if (loading || invitationLoading) {
    return <CenteredSpinner />;
  }

  // Show invitation accept error for logged-in users with email mismatch
  if (user && invitation?.isValid && acceptError) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center py-8">
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Impossible de rejoindre l'espace</AlertTitle>
            <AlertDescription className="mt-2">
              {acceptError}
            </AlertDescription>
          </Alert>
          
          <p className="text-muted-foreground text-center mb-4">
            Vous êtes connecté(e) en tant que <strong>{user.email}</strong>
          </p>
          
          <div className="flex flex-col gap-2 w-full">
            <Button 
              onClick={async () => {
                await supabase.auth.signOut();
                setAcceptError(null);
              }}
              className="w-full"
            >
              Se déconnecter et utiliser un autre compte
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/', { replace: true })}
              className="w-full"
            >
              Continuer vers le tableau de bord
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Show accepting invitation state for logged-in users
  if (user && invitation?.isValid && isAcceptingInvitation) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <h2 className="text-xl font-semibold mb-2">Validation de l'invitation...</h2>
          <p className="text-muted-foreground text-center">
            Vous rejoignez l'espace <strong>{invitation.workspaceName}</strong>
          </p>
        </div>
      </AuthLayout>
    );
  }

  // Expired invitation: redirect to dedicated screen with reissue request flow
  if (hasInviteParams && invitation?.isExpired) {
    const inviteId = searchParams.get('invite');
    const tokenParam = searchParams.get('token');
    return (
      <Navigate
        to={`/invitation/expired?invite=${inviteId}&token=${tokenParam}`}
        replace
      />
    );
  }

  // Show invalid (already accepted, etc.) invitation message
  if (hasInviteParams && invitation && !invitation.isValid) {
    return (
      <AuthLayout>
        <Link
          to="/auth"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </Link>

        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Invitation invalide</AlertTitle>
          <AlertDescription>
            {invitation.isAccepted
              ? "Cette invitation a déjà été acceptée."
              : "Cette invitation n'est plus valide."}
          </AlertDescription>
        </Alert>

        <div className="text-center">
          <p className="text-muted-foreground mb-4">
            Vous avez peut-être déjà un compte ?
          </p>
          <Button asChild>
            <Link to="/auth">Se connecter</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Show error state
  if (hasInviteParams && invitationError && !invitation) {
    return (
      <AuthLayout>
        <Link
          to="/auth"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </Link>

        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erreur</AlertTitle>
          <AlertDescription>
            {invitationError}
          </AlertDescription>
        </Alert>

        <div className="text-center">
          <Button asChild variant="outline">
            <Link to="/signup">Créer un compte</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Link
        to="/auth"
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour à la connexion
      </Link>

      {/* Invitation context banner */}
      {invitation?.isValid && (
        <Alert className="mb-6 border-primary/30 bg-primary/5">
          <Users className="h-4 w-4 text-primary" />
          <AlertTitle className="text-primary">Vous êtes invité(e) !</AlertTitle>
          <AlertDescription>
            Créez votre compte pour rejoindre l'équipe <strong>{invitation.workspaceName}</strong>.
          </AlertDescription>
        </Alert>
      )}

      <h1 className="text-3xl font-bold text-foreground mb-2">
        {invitation?.isValid ? 'Créer votre compte' : 'Créer un compte'}
      </h1>
      <p className="text-muted-foreground mb-8">
        {invitation?.isValid 
          ? `Rejoignez ${invitation.workspaceName} et commencez à collaborer`
          : 'Commencez à gérer vos tickets dès maintenant'
        }
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name fields */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">Prénom</Label>
            <Input
              id="firstName"
              type="text"
              placeholder="Jean"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={errors.firstName ? 'border-destructive' : ''}
              disabled={isSubmitting}
            />
            {errors.firstName && (
              <p className="text-sm text-destructive">{errors.firstName}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Nom</Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Dupont"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={errors.lastName ? 'border-destructive' : ''}
              disabled={isSubmitting}
            />
            {errors.lastName && (
              <p className="text-sm text-destructive">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Adresse email</Label>
          <Input
            id="email"
            type="email"
            placeholder="vous@exemple.com"
            value={email}
            onChange={(e) => {
              // Block email modification if invitation is valid
              if (invitation?.isValid) return;
              setEmail(e.target.value);
            }}
            className={errors.email ? 'border-destructive' : ''}
            disabled={isSubmitting || invitation?.isValid}
            readOnly={invitation?.isValid}
          />
          {invitation?.isValid && (
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-primary" />
              Email lié à votre invitation (non modifiable)
            </p>
          )}
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Mot de passe</Label>
          <div className="relative flex gap-1">
            <div className="relative flex-1">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <PasswordGeneratorButton 
              onGenerate={handlePasswordGenerated}
              disabled={isSubmitting}
            />
          </div>
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password}</p>
          )}
          <PasswordStrengthIndicator password={password} />
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          size="lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Chargement...' : invitation?.isValid ? "Créer mon compte et rejoindre" : "S'inscrire"}
        </Button>

        {/* Google signup. When an invitation is active we still expose Google
            so users whose account was created via Google (same email) can
            authenticate without forcing a password creation. */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">ou</span>
          </div>
        </div>

        <GoogleAuthButton
          mode={invitation?.isValid ? 'signin' : 'signup'}
          onClick={async () => {
            // Preserve invitation params across the OAuth round-trip so
            // useInvitation can pick them up and accept on return.
            const googleRedirect = invitation?.isValid
              ? `${window.location.origin}/signup?invite=${invitation.workspaceId}&token=${invitation.token}`
              : undefined;
            const { error } = await signInWithGoogle(googleRedirect);
            if (error) {
              toast({
                title: "Erreur",
                description: invitation?.isValid
                  ? "Impossible de se connecter avec Google"
                  : "Impossible de s'inscrire avec Google",
                variant: "destructive",
              });
            }
          }}
          disabled={isSubmitting}
        />
      </form>

      <div className="mt-6 text-center">
        {invitation?.isValid ? (
          <p className="text-sm text-muted-foreground">
            Vous avez déjà un compte ?{' '}
            <Link
              to={`/auth?invite=${invitation.workspaceId}&token=${invitation.token}`}
              className="text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Se connecter
            </Link>
          </p>
        ) : (
          <Link
            to="/auth"
            className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
          >
            Déjà un compte ? Se connecter
          </Link>
        )}
      </div>
    </AuthLayout>
  );
};

export default Signup;
